import { z } from 'zod';

const nonNegativeNumber = (label: string) =>
  z.coerce
    .number({ invalid_type_error: `${label} must be a number` })
    .min(0, { message: `${label} cannot be negative` });

export const packagesFormSchema = z
  .object({
    business_plan: z.object({
      name: z.string().min(1, { message: 'Package name is required' }),
      public: z.boolean().optional(),
      base_price: nonNegativeNumber('Base price'),
      available_minutes: nonNegativeNumber('Included call minutes'),
      additional_per_min_charge: nonNegativeNumber('Cost per additional minute'),
      available_warm_call_minutes: nonNegativeNumber('Included warm call minutes'),
      additional_warm_call_charge: nonNegativeNumber('Cost per additional warm call minute'),
      available_sms_segments: nonNegativeNumber('Included SMS segments'),
      additional_per_sms_segment_charge: nonNegativeNumber('Cost per additional SMS segment'),
      included_ai_tokens: nonNegativeNumber('Included AI tokens'),
      additional_per_1k_ai_tokens_charge: nonNegativeNumber('Cost per 1K additional tokens'),
      available_chats: nonNegativeNumber('Included AI chat messages'),
      additional_per_chat_charge: nonNegativeNumber('Cost per additional AI chat message'),
      included_locations: nonNegativeNumber('Included location'),
      additional_per_location_charge: nonNegativeNumber('Cost per additional location'),
      included_storage: nonNegativeNumber('Included storage'),
      additional_per_storage_charge: nonNegativeNumber('Cost per additional GB'),
      included_concurrency_per_location: nonNegativeNumber('Included concurrency per location'),
      price_details: z
        .object({
          multilingual_charge_per_min: z
            .object({
              enabled: z.boolean().optional(),
              unit_amount: z.coerce.number().optional()
            })
            .optional(),
          phone_number_charge: z
            .object({
              enabled: z.boolean().optional(),
              unit_amount: z.coerce.number().optional()
            })
            .optional(),
          integration_charge: z.object({ enabled: z.boolean().optional() }).optional()
        })
        .optional()
    })
  })
  .superRefine((data, ctx) => {
    const multilingual = data.business_plan.price_details?.multilingual_charge_per_min;
    if (multilingual?.enabled) {
      const amount = multilingual.unit_amount;
      if (amount === undefined || amount === null || Number.isNaN(Number(amount))) {
        ctx.addIssue({
          path: ['business_plan', 'price_details', 'multilingual_charge_per_min', 'unit_amount'],
          code: z.ZodIssueCode.custom,
          message: 'Price per minute is required'
        });
      } else if (amount < 0) {
        ctx.addIssue({
          path: ['business_plan', 'price_details', 'multilingual_charge_per_min', 'unit_amount'],
          code: z.ZodIssueCode.custom,
          message: 'Price per minute cannot be negative'
        });
      }
    }

    const phoneNumberCharge = data.business_plan.price_details?.phone_number_charge;
    if (phoneNumberCharge?.enabled) {
      const amount = phoneNumberCharge.unit_amount;
      if (amount === undefined || amount === null || Number.isNaN(Number(amount))) {
        ctx.addIssue({
          path: ['business_plan', 'price_details', 'phone_number_charge', 'unit_amount'],
          code: z.ZodIssueCode.custom,
          message: 'Surcharge amount is required'
        });
      } else if (amount < 0) {
        ctx.addIssue({
          path: ['business_plan', 'price_details', 'phone_number_charge', 'unit_amount'],
          code: z.ZodIssueCode.custom,
          message: 'Surcharge amount cannot be negative'
        });
      }
    }
  });
